import { isContentfulAsset, isSitesDamImageEntry, getAssetDescription, computeAspectRatio } from './common.js';

export const CONTENTFUL_IMAGES_CDN = 'https://images.sw.cdn.siemens.com/siemens-disw-assets/public/';
const DEFAULT_LOCALE = 'en-US';
const DEFAULT_IMGIX_PARAMS = {
    auto: 'format,compress',
};

/**
 * Generates an optimized image URL for the given source, which can be either a Contentful Asset or a "Sites: DAM Image" entry.
 * @param {import('./types').ImageAssetSource} source - The image source, either a Contentful Asset or a "Sites: DAM Image" entry.
 * @param {import('./types').ImageSizingOptions} [options] - Options for URL generation and optimization.
 * @returns {string|null} - The optimized image URL, or null if the source is invalid.
 */
const createImageUrl = (source, options = {}) => {
    if (isContentfulAsset(source)) {
        return createImageUrlForContentfulAsset(source, options);
    }

    if (isSitesDamImageEntry(source)) {
        return createImageUrlForSitesDamImage(source, options);
    }

    console.warn('createImageUrl: Unrecognized image source format', source);
    return null;
};

/**
 * Creates a normalized image shape, including the optimized URL, alt text, aspect ratio, and source type.
 * @param {import('./types').ImageAssetSource} source - The image source, either a Contentful Asset or a "Sites: DAM Image" entry.
 * @param {import('./types').ImageSizingOptions} [options] - Options for URL generation and optimization.
 * @returns {import('./types').ImageShape | null} - An object containing the image URL, alt text, aspect ratio, and source type, or null if the source is invalid.
 */
const createImageShape = (source, options = {}) => {
    if (!source) {
        return null;
    }
    const url = createImageUrl(source, options);
    if (!url) {
        return null;
    }
    return {
        url,
        alt: getAssetDescription(source),
        aspectRatio: getImageAspectRatio(source, options),
        sourceType: isContentfulAsset(source) ? 'contentful' : 'dam',
    };
};

/**
 * Computes the aspect ratio for a given image source, which can be either a Contentful Asset or a "Sites: DAM Image" entry.
 * @param {import('./types').ImageAssetSource} source - The image source, either a Contentful Asset or a "Sites: DAM Image" entry.
 * @param {import('./types').ImageSizingOptions} options - Options that may include an explicit aspectRatio.
 * @returns {string|null} - The aspect ratio in "W/H" format, or null if it cannot be determined.
 */
const getImageAspectRatio = (source, options = {}) => {
    if (isContentfulAsset(source)) {
        return getAspectRatioForContentfulAsset(source, options);
    }

    if (isSitesDamImageEntry(source)) {
        return getAspectRatioForSitesDamImage(source, options);
    }

    console.warn('getImageAspectRatio: Unrecognized image source format', source);
    return null;
};

// --------- Handle Contentful Asset ---------

/**
 * Converts image sizing options to Imgix parameters.
 * @param {import('./types').ImageSizingOptions} options
 * @returns {Record<string, string | number>}
 */
export const imageOptionsToImgixParams = options => {
    const params = {};

    if (typeof options.width === 'number') {
        params.w = Math.round(options.width);
    }
    if (typeof options.height === 'number') {
        params.h = Math.round(options.height);
    }
    if (typeof options.maxWidth === 'number') {
        params['max-w'] = Math.round(options.maxWidth);
    }
    if (typeof options.maxHeight === 'number') {
        params['max-h'] = Math.round(options.maxHeight);
    }
    if (isRatioOptionValid(options.aspectRatio)) {
        params.ar = options.aspectRatio;
    }
    if (options.fit) {
        params.fit = options.fit;
    }
    if (Array.isArray(options.crop) && options.crop.length > 0) {
        params.crop = options.crop.join(',');
    }
    if (typeof options.quality === 'number') {
        params.q = Math.max(1, Math.min(100, options.quality));
    }

    return { ...DEFAULT_IMGIX_PARAMS, ...params };
};

/**
 * @param {import('./types').ContentfulImageAsset} asset - Contentful Asset object
 * @param {import('./types').ImageSizingOptions} options
 * @returns {string|null}
 */
const createImageUrlForContentfulAsset = (asset, options) => {
    const locale = options.locale || DEFAULT_LOCALE;
    const file = asset.fields?.file?.[locale] || asset.fields?.file || null;
    if (!file || !file.fileName) {
        return null;
    }

    const imgixParams = imageOptionsToImgixParams(options);
    const imgixParamsString = new URLSearchParams(imgixParams).toString();

    return `${CONTENTFUL_IMAGES_CDN}${asset.sys.id}/${locale}/${encodeURIComponent(file.fileName)}?${imgixParamsString}`;
};

/**
 * Computes the aspect ratio for a Contentful Asset, either from the provided options or by calculating it from the asset's dimensions.
 * @param {import('./types').ContentfulImageAsset} asset - Contentful Asset object
 * @param {import('./types').ImageSizingOptions} options - Options that may include an explicit aspectRatio
 * @returns {string|null} - The aspect ratio in "W/H" format, or null if it cannot be determined
 */
const getAspectRatioForContentfulAsset = (asset, options) => {
    if (isRatioOptionValid(options.aspectRatio)) {
        return options.aspectRatio.replace(':', '/');
    }
    if (typeof options.width === 'number' && typeof options.height === 'number') {
        return computeAspectRatio(Math.round(options.width), Math.round(options.height));
    }
    const details = asset?.fields?.file?.details?.image;
    return details ? computeAspectRatio(details.width, details.height) : null;
};

// --------- Handle Sites: DAM Image entry ---------

/**
 * @param {import('./types').SitesDamImageEntry} entry - "Sites: DAM Image" content entry
 * @param {import('./types').ImageSizingOptions} options
 * @returns {string|null}
 */
const createImageUrlForSitesDamImage = (entry, options) => {
    const sourceFieldValue = entry.fields?.mediaSource;
    if (!sourceFieldValue || typeof sourceFieldValue !== 'object') {
        return null;
    }
    const asset = sourceFieldValue.asset;
    if (!asset || asset.type !== 'image' || !asset.file.url) {
        return null;
    }

    try {
        const assetUrl = new URL(asset.file.url);
        const imgixParams = imageOptionsToImgixParams(options);
        const imgixParamsString = new URLSearchParams(imgixParams).toString();

        return `${assetUrl.origin}/dam-asset/${asset.id}/${encodeURIComponent(asset.file.name)}?${imgixParamsString}`;
    } catch (error) {
        console.error('Error building optimized image URL for Sites DAM Image entry', { entry, options, error });
        return null;
    }
};

/**
 * Computes the aspect ratio for a "Sites: DAM Image" entry, either from the provided options or by calculating it from the associated asset's dimensions.
 * @param {import('./types').SitesDamImageEntry} entry - "Sites: DAM Image" content entry
 * @param {import('./types').ImageSizingOptions} options - Options that may include an explicit aspectRatio
 * @returns {string|null} - The aspect ratio in "W/H" format, or null if it cannot be determined
 */
const getAspectRatioForSitesDamImage = (entry, options) => {
    if (isRatioOptionValid(options.aspectRatio)) {
        return options.aspectRatio.replace(':', '/');
    }
    if (typeof options.width === 'number' && typeof options.height === 'number') {
        return computeAspectRatio(Math.round(options.width), Math.round(options.height));
    }
    const asset = entry.fields?.mediaSource?.asset;
    const details = asset?.file?.details;
    return details ? computeAspectRatio(details.width, details.height) : null;
};

// --------- Common utils ---------

/**
 * Validates whether a given aspect ratio string is in the correct format (e.g., "16:9") and represents positive dimensions.
 * @param {string} ratio - The aspect ratio string to validate.
 * @returns {boolean} - True if the ratio is valid, false otherwise.
 */
const isRatioOptionValid = ratio => {
    if (typeof ratio !== 'string') {
        return false;
    }
    const parts = ratio.split(':');
    if (parts.length !== 2) {
        return false;
    }
    const [width, height] = parts.map(Number);
    return !isNaN(width) && !isNaN(height) && width > 0 && height > 0;
};

export { createImageUrl, createImageShape };

export default {
    createImageUrl,
    createImageShape,
};
